import { useCallback, useEffect, useRef } from 'react';
import { http } from '../../lib/http';
import { xsrf } from './utils';
import { markVerifyForBooking } from '../../utils/emailVerification';

function mapIntent(b) {
  const base = {
    provider: b.provider || b.shopId,
    shopName: b.shopName,
    selectedDate: b.selectedDate ?? b.date,
    selectedHour: b.selectedHour ?? b.time,
    staffId: b.staffId ?? b.selectedStaffId,
    staffName: b.staffName ?? b.selectedStaffName,
  };
  if (Array.isArray(b.services) && b.services.length > 0) {
    return {
      ...base,
      services: b.services,
      totalPrice: b.totalPrice ?? b.services.reduce((s, x) => s + Number(x.price || 0), 0),
    };
  }
  return {
    ...base,
    serviceName: b.serviceName ?? b.selectedService,
    totalPrice: b.totalPrice ?? b.price ?? b.servicePrice,
  };
}

/**
 * Handles loading current booking, visibility/focus/location refresh, and creating booking from intent.
 * Returns loadBooking. Mutates setBooking, setBusy, setErr via callbacks.
 */
export function useDashboardBooking({
  setBooking,
  setBusy,
  setErr,
  booking,
  busyBooking,
  intent,
  intentFromStorage,
  clearBookingIntent,
  notifyForStatus,
  locationPathname,
  bookingRetryToken = 0,
  onEmailVerificationRequired,
}) {
  const postedRef = useRef(false);

  const loadBooking = useCallback(async () => {
    try {
      setBusy((s) => ({ ...s, booking: true }));
      const data = await http.getJson(`/api/bookings/current?_t=${Date.now()}`);
      setBooking(data);
      setErr((s) => ({ ...s, booking: '' }));
      if (data) notifyForStatus(data);
    } catch (e) {
      if (e.status === 404) {
        setBooking(null);
        setErr((s) => ({ ...s, booking: '' }));
      } else if (e.status === 401 || /unauthorized/i.test(e.message || '')) {
        setBooking(null);
        setErr((s) => ({ ...s, booking: 'UNAUTHORIZED' }));
      } else {
        setErr((s) => ({ ...s, booking: e.message }));
      }
    } finally {
      setBusy((s) => ({ ...s, booking: false }));
    }
  }, [notifyForStatus, setBooking, setBusy, setErr]);

  useEffect(() => {
    loadBooking();
  }, [loadBooking]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) loadBooking();
    };
    const handleFocus = () => loadBooking();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadBooking]);

  useEffect(() => {
    if (locationPathname === '/dashboard') {
      const retryLoad = async (attempt = 1, maxAttempts = 3) => {
        await loadBooking();
        if (attempt < maxAttempts) {
          setTimeout(() => retryLoad(attempt + 1, maxAttempts), 1000 * attempt);
        }
      };
      retryLoad();
    }
  }, [locationPathname, loadBooking]);

  useEffect(() => {
    if (!intent) return;

    if (bookingRetryToken > 0) {
      postedRef.current = false;
    }

    if (postedRef.current) return;
    if (busyBooking) return;
    if (booking && bookingRetryToken === 0) {
      postedRef.current = true;
      return;
    }

    (async () => {
      try {
        const sessionOk = await http.fetch('/api/profile', { method: 'GET' }).then((r) => r.ok).catch(() => false);
        if (!sessionOk) {
          postedRef.current = true;
          window.location.href = '/register';
          return;
        }
        const servicePart = Array.isArray(intent.services) && intent.services.length
          ? intent.services.map(s => s.serviceName).join(',')
          : (intent.serviceName || intent.selectedService || '');
        const idemKey = `${intent.provider}-${servicePart}-${intent.selectedDate || intent.date}-${intent.selectedHour || intent.time}-${(Date.now() / 30000) | 0}`;
        const res = await http.fetch('/api/bookings', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': xsrf(),
            'Idempotency-Key': idemKey,
          },
          body: JSON.stringify(mapIntent(intent)),
        });
        postedRef.current = true;
        if (!res.ok) {
          if (res.status === 401) {
            try {
              const errorData = await res.json();
              if (errorData.redirectTo) {
                window.location.href = errorData.redirectTo;
                return;
              }
            } catch {}
          }
          if (res.status === 403) {
            try {
              const errorData = await res.json();
              if (errorData.code === 'EMAIL_NOT_VERIFIED') {
                markVerifyForBooking();
                onEmailVerificationRequired?.();
                return;
              }
            } catch {}
          }
          let msg = `Booking failed (HTTP ${res.status})`;
          try {
            const ct = res.headers.get('content-type') || '';
            if (ct.includes('application/json')) {
              const j = await res.json();
              if (j?.details && (j.details.detail || j.details.title)) msg = j.details.detail || j.details.title;
              else if (j?.message) msg = j.message;
              else if (j?.error) {
                msg = j.error.startsWith('ERROR_') ? j.error.replace(/^ERROR_/, '').replace(/_/g, ' ').toLowerCase() : j.error;
                msg = msg.charAt(0).toUpperCase() + msg.slice(1);
              }
            } else msg = (await res.text()) || msg;
          } catch {}
          if (res.status === 400 && msg.includes('HTTP 400')) msg = 'Invalid request. Please check your booking details and try again.';
          alert(msg);
          return;
        }
        await loadBooking();
        if (intentFromStorage) clearBookingIntent();
      } catch (e) {
        postedRef.current = true;
        console.error('create booking failed', e);
        alert('Could not create booking. Please try again.');
      }
    })();
  }, [intent, booking, busyBooking, loadBooking, intentFromStorage, clearBookingIntent, bookingRetryToken, onEmailVerificationRequired]);

  return { loadBooking };
}
